import { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Shield, ShieldCheck, Cpu, BarChart3, Webhook, RefreshCw, UserPlus, Rocket, Search, Database, Mail, CreditCard, KeyRound, Zap, RotateCw, Globe, Users, ToggleLeft, ToggleRight, ChevronLeft, ChevronRight, Building2, Pencil, Image as ImageIcon, Activity, Lock, Gauge, Store } from 'lucide-react'
import toast from 'react-hot-toast'
import { countryName } from '@/data/countries'
import { useAuth } from '@/auth/AuthContext'
import {
  getPlatformAiSettings,
  updatePlatformAiSettings,
  setPlatformAgentCredential,
  getInstanceAnalytics,
  getIOFlowHealth,
  getIOFlowFailures,
  retryIOFlowDelivery,
  getReleaseStage,
  updateReleaseStage,
  getPlatformDataDb,
  updatePlatformDataDb,
  clearPlatformDataDb,
  testPlatformDataDb,
  getPlatformEmail,
  updatePlatformEmail,
  clearPlatformEmail,
  testPlatformEmail,
  getPlatformCaptcha,
  updatePlatformCaptcha,
  clearPlatformCaptcha,
  testPlatformCaptcha,
  getAgentSecurity,
  updateAgentSecurity,
  expireNonExpiringAgentTokens,
  getQueryExecution,
  updateQueryExecution,
  type QueryExecutionSettings,
  type AgentSecurityPolicy,
  type PlatformDataDb,
  type PlatformEmailSettings,
  type PlatformCaptchaSettings,
  getWaitlist,
  grantWaitlistAccess,
  getInstanceUsers,
  setInstanceUserStatus,
  unlockInstanceUser,
  resetInstanceUserMfa,
  type InstanceUser,
  getInstanceTenants,
  type InstanceTenant,
  startPlatformClaudeOauth,
  completePlatformClaudeOauth,
  cancelPlatformClaudeOauth,
  getPlatformClaudeOauthStatus,
  disconnectPlatformClaudeOauth,
  startPlatformCodexOauth,
  pollPlatformCodexOauth,
  cancelPlatformCodexOauth,
  getPlatformCodexOauthStatus,
  disconnectPlatformCodexOauth,
  type PlatformAiSettings,
  type InstanceAnalytics,
  type IOFlowHealth,
  type IOFlowFailure,
  type ReleaseStageInfo,
  type WaitlistEntry,
  type WaitlistAccountState,
  getIOFlowPublishRequests,
  approveIOFlowPublish,
  denyIOFlowPublish,
  type IOFlowPublishRequest,
} from '@/api/instanceAdmin'
import { clearAppConfigCache } from '@/api/config'
import AgentConnectSection from '@/components/settings/AgentConnectSection'
import { TASK_TYPES, TASK_LABELS, AGENTS, getProvider, type TaskType, type ProviderId } from '@/data/llmCatalog'
import { getLlmRecommendations } from '@/api/settings'
import { MapBoundariesManager } from '@/components/geo/MapBoundaries'
import TenantDetailsModal from '@/components/common/TenantDetailsModal'
import BillingAdminTab from './billing/BillingAdminTab'
import ImageGenerationTab from './ImageGenerationTab'
import EdgeTab from './EdgeTab'
import ResourceLimitsPanel from './ResourceLimitsPanel'
import MarketplaceAdminPanel from './MarketplaceAdminPanel'
import ActivityReport from '@twasta/page-runtime/activity/ActivityReport'
import { activityAdapter } from '@/api/activity'

/** Everything recorded on this instance — every organization's builders
 *  (incl. Ask-AI prompts), every application's users and website visitors. */
function InstanceActivityTab() {
  const adapter = useMemo(() => activityAdapter('instance'), [])
  return <ActivityReport adapter={adapter} showProject />
}

type Tab = 'ai' | 'image' | 'database' | 'resources' | 'email' | 'billing' | 'marketplace' | 'agent' | 'captcha' | 'edge' | 'access' | 'users' | 'tenants' | 'analytics' | 'activity' | 'ioflow' | 'approvals' | 'geo'

/**
 * The tabbed console body — the tab bar plus the active panel. Shared by the
 * full-page route (`InstanceAdminPage`) and the modal (`InstanceAdminModal`)
 * so both surfaces render exactly the same thing.
 */
export function InstanceAdminConsole() {
  const [tab, setTab] = useState<Tab>('ai')
  return (
    <>
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--color-border)', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <TabBtn active={tab === 'ai'} onClick={() => setTab('ai')} icon={<Cpu size={16} />} label="AI Settings" />
        <TabBtn active={tab === 'image'} onClick={() => setTab('image')} icon={<ImageIcon size={16} />} label="Image Generation" />
        <TabBtn active={tab === 'database'} onClick={() => setTab('database')} icon={<Database size={16} />} label="Database" />
        <TabBtn active={tab === 'resources'} onClick={() => setTab('resources')} icon={<Gauge size={16} />} label="Resource limits" />
        <TabBtn active={tab === 'email'} onClick={() => setTab('email')} icon={<Mail size={16} />} label="Email" />
        <TabBtn active={tab === 'billing'} onClick={() => setTab('billing')} icon={<CreditCard size={16} />} label="Billing" />
        <TabBtn active={tab === 'marketplace'} onClick={() => setTab('marketplace')} icon={<Store size={16} />} label="Marketplace" />
        <TabBtn active={tab === 'agent'} onClick={() => setTab('agent')} icon={<KeyRound size={16} />} label="Agent Security" />
        <TabBtn active={tab === 'captcha'} onClick={() => setTab('captcha')} icon={<Shield size={16} />} label="Spam Protection" />
        <TabBtn active={tab === 'edge'} onClick={() => setTab('edge')} icon={<Lock size={16} />} label="Edge & HTTPS" />
        <TabBtn active={tab === 'access'} onClick={() => setTab('access')} icon={<UserPlus size={16} />} label="Access & Waitlist" />
        <TabBtn active={tab === 'users'} onClick={() => setTab('users')} icon={<Users size={16} />} label="Users" />
        <TabBtn active={tab === 'tenants'} onClick={() => setTab('tenants')} icon={<Building2 size={16} />} label="Organizations" />
        <TabBtn active={tab === 'analytics'} onClick={() => setTab('analytics')} icon={<BarChart3 size={16} />} label="Analytics" />
        <TabBtn active={tab === 'activity'} onClick={() => setTab('activity')} icon={<Activity size={16} />} label="Activity" />
        <TabBtn active={tab === 'ioflow'} onClick={() => setTab('ioflow')} icon={<Webhook size={16} />} label="IOFlow Health" />
        <TabBtn active={tab === 'approvals'} onClick={() => setTab('approvals')} icon={<ShieldCheck size={16} />} label="IOFlow Approvals" />
        <TabBtn active={tab === 'geo'} onClick={() => setTab('geo')} icon={<Globe size={16} />} label="Map Boundaries" />
      </div>

      {tab === 'ai' && <AiSettingsTab />}
      {tab === 'activity' && <InstanceActivityTab />}
      {tab === 'image' && <ImageGenerationTab />}
      {tab === 'database' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
          <DataDbTab />
          {/* Its own load + own Save, deliberately: saving a timeout must not
              touch the credential form or trigger its engine-cache disposal. */}
          <QueryExecutionCard />
        </div>
      )}
      {tab === 'resources' && <ResourceLimitsPanel />}
      {tab === 'email' && <EmailTab />}
      {tab === 'billing' && <BillingAdminTab />}
      {tab === 'marketplace' && <MarketplaceAdminPanel />}
      {tab === 'agent' && <AgentSecurityTab />}
      {tab === 'captcha' && <CaptchaTab />}
      {tab === 'edge' && <EdgeTab />}
      {tab === 'access' && <AccessTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'tenants' && <TenantsTab />}
      {tab === 'analytics' && <AnalyticsTab />}
      {tab === 'ioflow' && <IOFlowHealthTab />}
      {tab === 'approvals' && <IOFlowApprovalsTab />}
      {tab === 'geo' && <MapBoundariesManager />}
    </>
  )
}

export default function InstanceAdminPage() {
  const { user } = useAuth()

  // Guard: only instance admins (env list) may view this console.
  if (user && !user.is_instance_admin) return <Navigate to="/workbench" replace />

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', width: '100%' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.5625rem', marginBottom: '0.25rem' }}>
        <ShieldCheck size={22} style={{ color: 'var(--color-accent)' }} /> Instance Admin
      </h1>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginBottom: '1.25rem' }}>
        Configure the platform-default AI, tenant database, outbound email and payment gateway,
        control the release stage and waitlist access, view instance-wide analytics, and monitor
        IOFlow integration delivery.
      </p>
      <InstanceAdminConsole />
    </div>
  )
}

function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.5rem 0.875rem',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
        color: active ? 'var(--color-text)' : 'var(--color-text-secondary)',
        fontWeight: active ? 600 : 400,
        fontSize: '0.9375rem',
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {icon}{label}
    </button>
  )
}

/* ── AI Settings ──────────────────────────────────────────────────── */

const PROVIDERS = ['anthropic', 'openai', 'google', 'deepseek']
const EFFORTS = ['low', 'medium', 'high']
const MODES = ['api', 'agent']

// Agent CLIs the PLATFORM DEFAULT can drive — every agent in the catalog.
const PLATFORM_AGENTS = AGENTS

/** The provider whose API key each agent consumes in api_key mode. */
const AGENT_PROVIDER: Record<string, string> = {
  claude_code: 'anthropic',
  codex: 'openai',
}

const PROVIDER_LABEL: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  deepseek: 'DeepSeek',
}

function AiSettingsTab() {
  const [s, setS] = useState<PlatformAiSettings | null>(null)
  const [apiKey, setApiKey] = useState('') // blank = leave unchanged
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [recommending, setRecommending] = useState(false)

  useEffect(() => {
    getPlatformAiSettings()
      .then(setS)
      .catch(() => toast.error('Failed to load platform AI settings'))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof PlatformAiSettings>(k: K, v: PlatformAiSettings[K]) => setS({ ...s, [k]: v })

  const isAgent = s.mode === 'agent'
  const authMode = s.auth || 'api_key'
  // In agent mode the provider follows the CLI; in API mode it is the choice.
  const effectiveProvider = isAgent ? (AGENT_PROVIDER[s.agent] || s.provider) : s.provider
  const agentLabel = AGENTS.find((a) => a.id === s.agent)?.label || 'the agent'
  const providerLabel = PROVIDER_LABEL[effectiveProvider] || effectiveProvider || 'the provider'

  // Models available for the effective provider (drives the Model dropdowns).
  const modelOpts = effectiveProvider ? (getProvider(effectiveProvider as ProviderId)?.models ?? []) : []
  const hasModel = (id: string) => modelOpts.some((m) => m.id === id)

  // Pre-fill the platform per-task table with the analysis-backed recommended
  // model + effort for the current provider/agent. Fills the fields only — the
  // admin can still tweak any row and must Save for it to take effect.
  const applyRecommended = async () => {
    if (!s) return
    setRecommending(true)
    try {
      const recs = await getLlmRecommendations(
        isAgent ? { agent: s.agent } : { provider: effectiveProvider },
      )
      const next: Record<string, { model?: string; effort?: string }> = { ...(s.task_llm_config || {}) }
      for (const task of TASK_TYPES) {
        const r = recs[task]
        if (!r) continue
        next[task] = { ...(next[task] || {}), model: r.model, effort: r.effort }
      }
      set('task_llm_config', next)
      toast.success('Recommended models applied — review and Save')
    } catch {
      toast.error('Could not load recommended models')
    } finally {
      setRecommending(false)
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      const updated = await updatePlatformAiSettings({
        mode: s.mode,
        // In agent mode the provider is not a free choice — it is the one
        // whose API key that CLI consumes, and the backend's api_key path
        // still matches on it. Keep the two in step on save.
        provider: s.mode === 'agent' ? AGENT_PROVIDER[s.agent] || s.provider : s.provider,
        agent: s.agent,
        model: s.model,
        effort: s.effort,
        auth: s.auth || 'api_key',
        task_llm_config: s.task_llm_config || {},
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      })
      setS(updated)
      setApiKey('')
      toast.success('Platform AI settings saved')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Platform-default AI</h3>
        {/* The bundle + credentials are re-read on each run; saves are live. */}
        <ApplyBadge apply="live" />
      </div>
      <Field label="Mode">
        <select value={s.mode} onChange={(e) => set('mode', e.target.value)} style={{ width: '100%' }}>
          {MODES.map((m) => <option key={m} value={m}>{m === 'api' ? 'LLM API call' : 'Agent'}</option>)}
        </select>
      </Field>
      {isAgent && (
        <Field label="Agent">
          <select value={s.agent} onChange={(e) => set('agent', e.target.value)} style={{ width: '100%' }}>
            {PLATFORM_AGENTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
            The CLI every user riding the platform default runs. Users who configure their own
            LLM settings use theirs instead.
          </p>
        </Field>
      )}
      {isAgent && (
        <Field label="Authentication">
          <select value={s.auth || 'api_key'} onChange={(e) => set('auth', e.target.value)} style={{ width: '100%' }}>
            <option value="subscription">Subscription account</option>
            <option value="api_key">API key</option>
          </select>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
            <strong>Subscription</strong> runs on the {agentLabel} account connected below
            (falling back to the server's ambient CLI login when none is connected);{' '}
            <strong>API key</strong> runs on the {providerLabel} API key below.
          </p>
        </Field>
      )}
      {/* Subscription — both agent CLIs have an in-app connect flow, each on
          the OAuth path it sanctions: Claude pastes back a code, Codex signs
          in with a one-time device code. Any other agent has no equivalent, so
          the admin pastes the CLI's own credentials file, exactly as
          Settings → LLM asks a user to. */}
      {isAgent && authMode === 'subscription' && s.agent === 'claude_code' && (
        <Field label="Platform Claude account">
          <AgentConnectSection
            provider="Claude"
            isSet={!!s.has_claude_oauth}
            onStored={(isSet) => {
              setS({ ...s, has_claude_oauth: isSet,
                     agent_credentials: { ...s.agent_credentials, claude_code: isSet } })
            }}
            api={{
              kind: 'paste',
              start: startPlatformClaudeOauth,
              complete: completePlatformClaudeOauth,
              cancel: cancelPlatformClaudeOauth,
              status: getPlatformClaudeOauthStatus,
              disconnect: disconnectPlatformClaudeOauth,
            }}
            disconnectConfirm="Disconnect the platform Claude account? Every user riding the platform default falls back to the server's ambient CLI login until you reconnect."
            footer={
              <>
                Saved as the <strong>platform default</strong> immediately — no separate Save needed.
                Usage for every user without their own Claude credential bills to this plan.
              </>
            }
          />
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.5rem 0 0' }}>
            Connect the Claude subscription (Pro / Max / Team) that should back the platform default.
            Users who connect their own Claude account in Settings always use theirs instead.
          </p>
        </Field>
      )}
      {isAgent && authMode === 'subscription' && s.agent === 'codex' && (
        <Field label="Platform Codex account">
          <AgentConnectSection
            provider="Codex"
            isSet={!!s.agent_credentials?.codex}
            onStored={(isSet) => {
              setS({ ...s, agent_credentials: { ...s.agent_credentials, codex: isSet } })
            }}
            api={{
              kind: 'device',
              start: startPlatformCodexOauth,
              poll: pollPlatformCodexOauth,
              cancel: cancelPlatformCodexOauth,
              status: getPlatformCodexOauthStatus,
              disconnect: disconnectPlatformCodexOauth,
            }}
            disconnectConfirm="Disconnect the platform Codex account? Every user riding the platform default falls back to the platform API key (or the server's ambient env) until you reconnect."
            footer={
              <>
                Saved as the <strong>platform default</strong> immediately — no separate Save needed.
                Usage for every user without their own Codex credential bills to this plan.
              </>
            }
          />
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.5rem 0 0' }}>
            Connect the ChatGPT subscription (Plus / Pro / Business) that should back the platform
            default — you sign in with a one-time code on OpenAI's page. Users who connect their own
            Codex account in Settings always use theirs instead.
          </p>
        </Field>
      )}
      {isAgent && authMode === 'subscription'
        && s.agent !== 'claude_code' && s.agent !== 'codex' && (
        <AgentCredentialField
          agent={s.agent}
          label={agentLabel}
          isSet={!!s.agent_credentials?.[s.agent]}
          onStored={(updated) => setS(updated)}
        />
      )}
      {/* API-mode picks its provider freely; agent mode's provider is fixed by
          the CLI, so showing a dropdown there would just invite a mismatch. */}
      {!isAgent ? (
        <Field label="Provider">
          <select value={s.provider} onChange={(e) => set('provider', e.target.value)} style={{ width: '100%' }}>
            <option value="">— select —</option>
            {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
      ) : (
        <Field label="Provider">
          <input value={providerLabel} disabled style={{ width: '100%' }} />
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
            Fixed by the agent — {agentLabel} runs on {providerLabel} models.
          </p>
        </Field>
      )}
      <Field label="Model">
        <select value={s.model} onChange={(e) => set('model', e.target.value)} disabled={!effectiveProvider} style={{ width: '100%' }}>
          <option value="">{effectiveProvider ? '— select model —' : '— select a provider first —'}</option>
          {modelOpts.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          {s.model && !hasModel(s.model) && <option value={s.model}>{s.model} (custom)</option>}
        </select>
      </Field>
      {/* The API key is what api mode always uses, and what agent mode uses in
          "API key" auth — hidden under agent + subscription, where the
          connected account above is the credential. */}
      {(!isAgent || authMode === 'api_key') && (
        <Field label={`${providerLabel} API key ${s.has_api_key ? '(configured — leave blank to keep)' : '(not set)'}`}>
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={s.has_api_key ? '••••••••' : `Paste the platform ${providerLabel} API key`} style={{ width: '100%' }} />
          {isAgent && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
              Used when it matches the running CLI's provider; otherwise the CLI falls back to the
              server's ambient key.
            </p>
          )}
        </Field>
      )}
      <Field label="Default thinking level (effort)">
        <select value={s.effort} onChange={(e) => set('effort', e.target.value)} style={{ width: '100%' }}>
          {EFFORTS.map((ef) => <option key={ef} value={ef}>{ef}</option>)}
        </select>
      </Field>

      <div>
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
          Per-task model &amp; thinking
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 0.5rem' }}>
          Optional overrides on top of the default model/effort above — e.g. FRS on the
          biggest model, business logic on a faster one. These apply to every user riding
          the platform default and can only be changed here.
        </p>
        <div style={{ marginBottom: '0.5rem' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={applyRecommended}
            disabled={recommending || !effectiveProvider}
            title="Fill each task with the cost-optimised recommended model and effort for this provider/agent"
          >
            {recommending ? 'Loading…' : 'Use recommended'}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {TASK_TYPES.map((task) => {
            const cfg = s.task_llm_config?.[task] || {}
            const setTask = (patch: { model?: string; effort?: string }) => {
              const next = { ...(s.task_llm_config || {}) }
              const merged = { ...cfg, ...patch }
              // Drop empty overrides so the row falls back to the default.
              if (!merged.model && !merged.effort) delete next[task]
              else next[task] = merged
              set('task_llm_config', next)
            }
            return (
              <div key={task} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 130px', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem' }}>{TASK_LABELS[task as TaskType] || task}</span>
                <select
                  value={cfg.model || ''}
                  onChange={(e) => setTask({ model: e.target.value })}
                  disabled={!s.provider}
                  style={{ width: '100%' }}
                >
                  <option value="">default ({s.model || 'platform model'})</option>
                  {modelOpts.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  {cfg.model && !hasModel(cfg.model) && <option value={cfg.model}>{cfg.model} (custom)</option>}
                </select>
                <select value={cfg.effort || ''} onChange={(e) => setTask({ effort: e.target.value })} style={{ width: '100%' }}>
                  <option value="">default ({s.effort})</option>
                  {EFFORTS.map((ef) => <option key={ef} value={ef}>{ef}</option>)}
                </select>
              </div>
            )
          })}
        </div>
      </div>

      <div style={{
        fontSize: '0.875rem',
        color: 'var(--color-text-secondary)',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '0.75rem 0.875rem',
        lineHeight: 1.5,
      }}>
        Platform-key usage is capped per subscription plan (Free / Pro / Enterprise)
        by the built-in monthly limits. Users who configure their own API key are
        not limited.
      </div>

      <div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
      {s.updated_at && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: 0 }}>
          Last updated {new Date(s.updated_at).toLocaleString()} {s.updated_by ? `by ${s.updated_by}` : ''}
        </p>
      )}
    </div>
  )
}

/**
 * Platform subscription credential for an agent without an in-app OAuth flow
 * (Codex, Gemini): the admin pastes the CLI's own credentials file, the same
 * thing Settings → LLM asks a user for. Stored immediately on its own button —
 * it is not part of the AI-settings draft.
 */
function AgentCredentialField({
  agent, label, isSet, onStored,
}: {
  agent: string
  label: string
  isSet: boolean
  onStored: (updated: PlatformAiSettings) => void
}) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const file = '~/.codex/auth.json'

  const store = async (credential: string) => {
    setBusy(true)
    try {
      onStored(await setPlatformAgentCredential(agent, credential))
      setValue('')
      toast.success(credential ? `${label} account connected` : `${label} account disconnected`)
    } catch (e: unknown) {
      toast.error(errText(e, 'Failed to store the credential'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Field label={`Platform ${label} account ${isSet ? '(connected)' : '(not connected)'}`}>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        placeholder={isSet
          ? 'Connected — paste a new file to replace it'
          : `Paste the contents of ${file}`}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8125rem' }}
      />
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => store(value.trim())} disabled={busy || !value.trim()}>
          {busy ? 'Saving…' : isSet ? 'Replace credential' : 'Connect account'}
        </button>
        {isSet && (
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => {
              if (!window.confirm(
                `Disconnect the platform ${label} account? Every user riding the platform ` +
                `default falls back to the server's ambient CLI login until you reconnect.`,
              )) return
              store('')
            }}
          >
            Disconnect
          </button>
        )}
      </div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
        {label} has no in-app connect flow, so run its login on any machine and paste
        <code> {file}</code> here. Stored encrypted and applied immediately — no separate Save
        needed. Usage for every user without their own {label} credential bills to this account.
      </p>
    </Field>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
      <span style={{ display: 'block', marginBottom: '0.25rem' }}>{label}</span>
      {children}
    </label>
  )
}

/* ── Platform data database ───────────────────────────────────────── */
// The Postgres server hosting twasta_preview_<tenant> for every project that
// has no connection of its own. Until an admin saves here, the platform parses
// DATABASE_URL (the auth/identity URL) — so this form opens pre-filled with the
// auth server's host/port and the suggested `twaadmin` login, and saving is
// what actually switches the instance over.

function DataDbTab() {
  const [s, setS] = useState<PlatformDataDb | null>(null)
  const [password, setPassword] = useState('') // blank = leave stored one
  // Which source the form is showing. Seeded from the server's `use_env`; the
  // checkbox flips it. Custom values aren't persisted until Save, and env isn't
  // restored until Save, so this is purely the editing mode.
  const [useEnv, setUseEnv] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    getPlatformDataDb()
      .then((d) => { setS(d); setUseEnv(d.use_env) })
      .catch(() => toast.error('Failed to load the platform database settings'))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof PlatformDataDb>(k: K, v: PlatformDataDb[K]) => {
    setS({ ...s, [k]: v })
    setTestResult(null)
  }

  const env = s.env
  const envSummary = `${env.username || '?'}@${env.host || '?'}:${env.port || 5432}` +
    (env.database ? `/${env.database}` : '')

  const payload = () => ({
    host: s.host.trim(),
    port: Number(s.port) || 5432,
    username: s.username.trim(),
    admin_db: s.admin_db.trim(),
    ...(password.trim() ? { password: password.trim() } : {}),
  })

  const test = async () => {
    if (!s.host.trim()) return toast.error('Database host is required')
    setTesting(true)
    setTestResult(null)
    try {
      setTestResult(await testPlatformDataDb(payload()))
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Test failed'
      setTestResult({ success: false, message: msg })
    } finally {
      setTesting(false)
    }
  }

  const save = async () => {
    if (!s.host.trim()) return toast.error('Database host is required')
    // Switching the whole instance onto a different login is not a change to
    // make by mis-click — every project without its own connection follows it.
    if (!window.confirm(
      `Point every project without its own database connection at ` +
      `${s.username.trim() || 'twaadmin'}@${s.host.trim()}:${s.port || 5432}?\n\n` +
      `Existing connection pools are dropped so this takes effect immediately.`,
    )) return
    setSaving(true)
    try {
      setS(await updatePlatformDataDb(payload()))
      setPassword('')
      setTestResult(null)
      toast.success('Platform database saved')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  // Checking "use env" while a custom connection is stored reverts on Save.
  const applyEnv = async () => {
    if (!s.configured) return // already on env — nothing to persist
    if (!window.confirm(
      'Switch back to the .env DATABASE_URL database?\n\n' +
      'Every project without its own connection goes back to the ' +
      `.env login (${envSummary}).`,
    )) return
    setSaving(true)
    try {
      const d = await clearPlatformDataDb()
      setS(d)
      setUseEnv(d.use_env)
      setPassword('')
      setTestResult(null)
      toast.success('Now using the .env database')
    } catch {
      toast.error('Failed to switch to the .env database')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Tenant data database</h3>
        {/* .env source needs a restart; a console connection is disposed + live. */}
        <ApplyBadge apply={useEnv ? 'restart' : 'live'} />
      </div>
      <InfoBox>
        The Postgres server that hosts every tenant&rsquo;s application data
        (<code>twasta_preview_&lt;tenant&gt;</code>). It is used by any project that has
        no database connection of its own in Database Designer &rarr; Change database.
      </InfoBox>

      {/* Source toggle: the .env DATABASE_URL, or a connection configured here. */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9375rem' }}>
        <input
          type="checkbox"
          checked={useEnv}
          onChange={(e) => { setUseEnv(e.target.checked); setTestResult(null) }}
          style={{ marginTop: 3 }}
        />
        <span>
          Use the <code>.env</code> database (<code>DATABASE_URL</code>)
          <span style={{
            marginLeft: '0.5rem',
            fontSize: '0.8125rem',
            fontWeight: 600,
            padding: '0.1rem 0.4rem',
            borderRadius: 4,
            border: '1px solid var(--color-border)',
            color: s.configured ? 'var(--color-text-secondary)' : 'var(--color-success, #009974)',
          }}>
            {s.configured ? 'CUSTOM CONNECTION ACTIVE' : 'CONFIGURED IN .ENV'}
          </span>
        </span>
      </label>

      {useEnv ? (
        <>
          <Field label="Configured in .env (DATABASE_URL)">
            <input value={envSummary} disabled style={{ width: '100%' }} />
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
              Read from <code>backend/.env</code>. Change it there and restart the backend, or
              uncheck the box to point the instance at a database from this console instead.
            </p>
          </Field>
          {s.configured && (
            <div>
              <button className="btn btn-primary" onClick={applyEnv} disabled={saving}>
                {saving ? 'Switching…' : 'Switch to the .env database'}
              </button>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
                A custom connection is currently active. Applying this drops the stored
                connection and reverts every project without its own database to
                <code> {envSummary}</code>.
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <Field label="Host">
            <input value={s.host} onChange={(e) => set('host', e.target.value)} placeholder={s.defaults.host} style={{ width: '100%' }} />
          </Field>
          <Field label="Port">
            <input type="number" value={s.port} onChange={(e) => set('port', Number(e.target.value))} style={{ width: '100%' }} />
          </Field>
          <Field label="Username">
            <input value={s.username} onChange={(e) => set('username', e.target.value)} placeholder={s.defaults.username} style={{ width: '100%' }} />
          </Field>
          <Field label={`Password ${s.has_password ? '(configured — leave blank to keep)' : '(not set)'}`}>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setTestResult(null) }}
              placeholder={s.has_password ? '••••••••' : 'Password for this login'}
              style={{ width: '100%' }}
            />
          </Field>
          <Field label="Maintenance database">
            <input value={s.admin_db} onChange={(e) => set('admin_db', e.target.value)} placeholder={s.defaults.admin_db} style={{ width: '100%' }} />
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
              Connected to only in order to <code>CREATE</code> each tenant&rsquo;s database, so this
              login needs <code>CREATEDB</code>. Never a tenant database itself.
            </p>
          </Field>

          <TestBanner result={testResult} />

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={test} disabled={testing || saving}>
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving || testing}>
              {saving ? 'Saving…' : 'Save database'}
            </button>
          </div>

          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
            Saving drops the cached connection pools, so the change is live without a restart.
            Projects that carry their own connection are unaffected. If this login points at a
            different server from the current one, tenants get fresh, empty databases there —
            move the data across before switching.
          </p>
        </>
      )}
    </div>
  )
}

/* ── Query execution ──────────────────────────────────────────────── */
// The statement timeout every generated analytical query runs under. It sits
// in the Database tab but is a separate card with its own load and its own
// Save, because the data-db PUT disposes engine pools and re-tests the stored
// credential — none of which should happen just because a timeout changed.

function QueryExecutionCard() {
  const [s, setS] = useState<QueryExecutionSettings | null>(null)
  // STRING state, like the agent-security numbers: blank has to be
  // representable, and a DEFAULTED 60 must not be saved back as an explicit 60.
  const [timeout_, setTimeout_] = useState('')
  const [maxTimeout, setMaxTimeout] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const seed = (d: QueryExecutionSettings) => {
    setS(d)
    setTimeout_(d.explicit?.query_timeout_seconds ? String(d.query_timeout_seconds ?? '') : '')
    setMaxTimeout(
      d.explicit?.max_query_timeout_seconds ? String(d.max_query_timeout_seconds ?? '') : '')
  }

  useEffect(() => {
    getQueryExecution()
      .then(seed)
      .catch(() => toast.error('Failed to load the query execution settings'))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const save = async () => {
    // 0 CLEARS the field back to the built-in defaults — which is exactly
    // what an emptied input means, so send it rather than omitting it.
    const secs = parseInt(timeout_, 10) || 0
    const maxSecs = parseInt(maxTimeout, 10) || 0
    // The server rejects this too; failing here just makes it immediate.
    if (secs && maxSecs && secs > maxSecs) {
      toast.error(
        `The query timeout (${secs}s) cannot be greater than the maximum (${maxSecs}s)`)
      return
    }
    setSaving(true)
    try {
      seed(await updateQueryExecution({
        query_timeout_seconds: secs,
        max_query_timeout_seconds: maxSecs,
      }))
      toast.success('Query execution settings saved')
    } catch (e: unknown) {
      toast.error(errText(e, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Query execution</h3>
        <ApplyBadge apply="live" />
      </div>

      <Field label="Query timeout (seconds)">
        <input
          type="number"
          min={1}
          value={timeout_}
          onChange={(e) => setTimeout_(e.target.value)}
          placeholder={`${s.defaults?.query_timeout_seconds ?? 60}`}
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          Any generated analytical query (dashboards, visuals, reports, Ask-AI queries) running
          longer than this is cancelled. Ask-AI is told to optimize and retry. Blank = built-in
          defaults (60s analytics / 15s design-query preview).
        </p>
      </Field>

      <Field label="Maximum query timeout (seconds)">
        <input
          type="number"
          min={1}
          value={maxTimeout}
          onChange={(e) => setMaxTimeout(e.target.value)}
          placeholder={`${s.defaults?.max_query_timeout_seconds ?? 600}`}
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          The ceiling a project connection may not exceed when it overrides the timeout with its
          own value. Blank = 600.
        </p>
      </Field>

      <div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save query execution'}
        </button>
      </div>
    </div>
  )
}

/** Result of a "Test connection" / "Send test email" round trip. */
function TestBanner({ result }: { result: { success: boolean; message: string } | null }) {
  if (!result) return null
  return (
    <div style={{
      fontSize: '0.875rem',
      borderRadius: 6,
      padding: '0.625rem 0.75rem',
      lineHeight: 1.5,
      border: '1px solid var(--color-border)',
      color: result.success ? 'var(--color-success, #009974)' : 'var(--color-danger, #d64545)',
      background: 'var(--color-bg-secondary)',
    }}>
      {result.message}
    </div>
  )
}

/** The shaded explainer box each settings tab opens with. */
function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '0.875rem',
      color: 'var(--color-text-secondary)',
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 6,
      padding: '0.75rem 0.875rem',
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

/** Pull an API error's `detail` out, with a fallback. */
function errText(e: unknown, fallback: string) {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback
}

/**
 * Whether a setting takes effect immediately or needs a backend restart.
 * Almost everything in this console is applied live — values are re-read per
 * request and engine pools are refreshed on save. The one exception is a value
 * sourced from `backend/.env` (e.g. the DATABASE_URL fallback): editing that
 * file only takes effect after a restart.
 */
function ApplyBadge({ apply, children }: { apply: 'live' | 'restart'; children?: React.ReactNode }) {
  const live = apply === 'live'
  const [Icon, label, color] = live
    ? [Zap, 'Applies immediately', 'var(--color-success, #009974)']
    : [RotateCw, 'Restart required', 'var(--color-warning, #b7791f)']
  return (
    <span
      title={live
        ? 'Saved changes take effect right away — no backend restart needed.'
        : 'This value is read at startup — restart the backend for a change to take effect.'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: '0.8125rem',
        fontWeight: 600,
        padding: '0.15rem 0.45rem',
        borderRadius: 4,
        border: `1px solid ${color}`,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={12} /> {children || label}
    </span>
  )
}

/* ── Outbound email (SMTP) ────────────────────────────────────────── */
// Console-owned overrides on the MAIL_* env settings. Until an admin saves,
// the instance runs on env and this form shows those values.

function EmailTab() {
  const [s, setS] = useState<PlatformEmailSettings | null>(null)
  const [password, setPassword] = useState('') // blank = leave stored one
  const [testTo, setTestTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    getPlatformEmail()
      .then(setS)
      .catch(() => toast.error('Failed to load the email settings'))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof PlatformEmailSettings>(k: K, v: PlatformEmailSettings[K]) => {
    setS({ ...s, [k]: v })
    setTestResult(null)
  }

  const payload = () => ({
    server: s.server.trim(),
    port: Number(s.port) || 587,
    use_tls: s.use_tls,
    username: s.username.trim(),
    from_address: s.from_address.trim(),
    ...(password.trim() ? { password: password.trim() } : {}),
  })

  const test = async () => {
    const to = testTo.trim()
    if (!to) return toast.error('Enter an address to send the test to')
    if (!s.server.trim()) return toast.error('SMTP server is required')
    setTesting(true)
    setTestResult(null)
    try {
      setTestResult(await testPlatformEmail({ ...payload(), to }))
    } catch (e: unknown) {
      setTestResult({ success: false, message: errText(e, 'Test failed') })
    } finally {
      setTesting(false)
    }
  }

  const save = async () => {
    if (!s.server.trim()) return toast.error('SMTP server is required')
    setSaving(true)
    try {
      setS(await updatePlatformEmail(payload()))
      setPassword('')
      setTestResult(null)
      toast.success('Email settings saved')
    } catch (e: unknown) {
      toast.error(errText(e, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  const revert = async () => {
    if (!window.confirm('Revert to the MAIL_* settings from backend/.env?')) return
    setSaving(true)
    try {
      setS(await clearPlatformEmail())
      setPassword('')
      setTestResult(null)
      toast.success('Reverted to the environment settings')
    } catch {
      toast.error('Failed to revert')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Outbound email</h3>
        {/* Resolved per send — a save is live for the next message. */}
        <ApplyBadge apply="live" />
      </div>
      <InfoBox>
        The SMTP account every outbound message is sent from — activation links, waitlist
        grants, password resets and team invitations.
        {!s.configured && (
          <>
            {' '}<strong>Not configured yet</strong> &mdash; the instance is using the
            <code> MAIL_*</code> settings from <code>backend/.env</code>, shown below.
            Saving takes over from them.
          </>
        )}
      </InfoBox>

      <Field label="SMTP server">
        <input value={s.server} onChange={(e) => set('server', e.target.value)} placeholder={s.defaults.server} style={{ width: '100%' }} />
      </Field>
      <Field label="Port">
        <input type="number" value={s.port} onChange={(e) => set('port', Number(e.target.value))} style={{ width: '100%' }} />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
        <input type="checkbox" checked={s.use_tls} onChange={(e) => set('use_tls', e.target.checked)} />
        Use STARTTLS
      </label>
      <Field label="Username">
        <input value={s.username} onChange={(e) => set('username', e.target.value)} placeholder={s.defaults.username || 'SMTP login'} style={{ width: '100%' }} />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          Leave blank for a relay that accepts unauthenticated submission.
        </p>
      </Field>
      <Field label={`Password ${s.has_password ? '(configured — leave blank to keep)' : '(not set)'}`}>
        <input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setTestResult(null) }}
          placeholder={s.has_password ? '••••••••' : 'SMTP password'}
          style={{ width: '100%' }}
        />
      </Field>
      <Field label="From address">
        <input value={s.from_address} onChange={(e) => set('from_address', e.target.value)} placeholder={s.defaults.from_address} style={{ width: '100%' }} />
      </Field>

      <Field label="Send a test email to">
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            style={{ flex: 1 }}
          />
          <button className="btn btn-secondary" onClick={test} disabled={testing || saving}>
            {testing ? 'Sending…' : 'Send test'}
          </button>
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          Sends with the values above without saving them, so a wrong password shows up
          here rather than in bounced activation mail.
        </p>
      </Field>

      <TestBanner result={testResult} />

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving || testing}>
          {saving ? 'Saving…' : 'Save email settings'}
        </button>
        {s.configured && (
          <button className="btn btn-secondary" onClick={revert} disabled={saving || testing}>
            Revert to environment
          </button>
        )}
      </div>
    </div>
  )
}

/* ── reCAPTCHA (the site-level bot check) ─────────────────────────── */
/**
 * The instance-wide FALLBACK key pair, protecting the public entry points of
 * every project that has not configured its own in Advanced → Spam protection:
 * a smart-page form, self-registration and password reset, a write through a
 * public embedded transaction, and a public file upload.
 *
 * The one thing this panel has to say plainly is what it does NOT cover. A
 * reCAPTCHA v3 key is bound to a DOMAIN LIST in Google's console, so this pair
 * covers the hostnames named there — this platform's own — and cannot cover a
 * project served at a customer's domain. Discovering that from a project whose
 * every genuine enquiry is suddenly flagged is the failure worth preventing
 * here.
 */

function CaptchaTab() {
  const [s, setS] = useState<PlatformCaptchaSettings | null>(null)
  const [secret, setSecret] = useState('')   // blank = leave the stored one
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    getPlatformCaptcha()
      .then(setS)
      .catch(() => toast.error('Failed to load the reCAPTCHA settings'))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof PlatformCaptchaSettings>(k: K, v: PlatformCaptchaSettings[K]) => {
    setS({ ...s, [k]: v })
    setTestResult(null)
  }

  const save = async () => {
    setSaving(true)
    try {
      setS(await updatePlatformCaptcha({
        site_key: s.site_key.trim(),
        min_score: Number(s.min_score),
        ...(secret.trim() ? { secret_key: secret.trim() } : {}),
      }))
      setSecret('')
      setTestResult(null)
      toast.success('reCAPTCHA settings saved')
    } catch (e: unknown) {
      toast.error(errText(e, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      setTestResult(await testPlatformCaptcha())
      setS(await getPlatformCaptcha())
    } catch (e: unknown) {
      setTestResult({ success: false, message: errText(e, 'Check failed') })
    } finally {
      setTesting(false)
    }
  }

  const clear = async () => {
    if (!window.confirm(
      'Turn the site-level bot check off?\n\n'
      + 'Every project that has not configured its OWN key stops checking its '
      + 'public forms, sign-up, embedded transaction saves and file uploads. '
      + 'Projects holding their own keys are unaffected.',
    )) return
    setSaving(true)
    try {
      setS(await clearPlatformCaptcha())
      setSecret('')
      setTestResult(null)
      toast.success('Site-level reCAPTCHA turned off')
    } catch {
      toast.error('Failed to clear')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Site-level reCAPTCHA</h3>
        {/* Resolved per request behind a 60-second cache — a save is live
            within a minute, and sooner for any project not mid-cache. */}
        <ApplyBadge apply="live" />
      </div>
      <InfoBox>
        The default bot check for every application on this instance. It guards
        the four doors a visitor can use without signing in: a smart-page form,
        self-registration and password reset, a save from a public embedded
        transaction, and a public file upload. reCAPTCHA <strong>v3</strong>
        {' '}scores each attempt in the background — a real visitor sees nothing.
        {!s.configured && (
          <>
            {' '}<strong>Not configured</strong> &mdash; public forms across this
            instance are guarded only by a hidden field and a timing probe,
            which a script written for the form defeats.
          </>
        )}
        {s.configured && !s.usable && (
          <>
            {' '}<strong>Incomplete</strong> &mdash; a site key is stored with no
            secret, so the check would run in every browser and never be
            verified. It is treated as off.
          </>
        )}
      </InfoBox>

      <InfoBox>
        <strong>This pair covers this platform&apos;s own hostnames.</strong> A
        reCAPTCHA key is bound to a domain list in Google&apos;s console, so an
        application served at a domain its own operator owns
        (<code>erp.acme.com</code>) must set its own key in Advanced &rarr; Spam
        protection — otherwise the check fails for every visitor at that
        address. A project that sets one overrides this pair entirely.
      </InfoBox>

      <Field label="Site key">
        <input
          value={s.site_key}
          onChange={(e) => set('site_key', e.target.value)}
          placeholder="6Lc…"
          autoComplete="off"
          spellCheck={false}
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          Printed in the page&apos;s own HTML — the browser cannot execute
          without it, so it is public by nature and is not encrypted.
        </p>
      </Field>

      <Field label={`Secret key ${s.has_secret ? '(configured — leave blank to keep)' : '(not set)'}`}>
        <input
          type="password"
          value={secret}
          onChange={(e) => { setSecret(e.target.value); setTestResult(null) }}
          placeholder={s.has_secret ? '••••••••' : '6Lc…'}
          autoComplete="new-password"
          spellCheck={false}
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          Encrypted at rest and never returned to this screen.
        </p>
      </Field>

      <Field label={`Suspect below ${Number(s.min_score).toFixed(1)}`}>
        <input
          type="range" min={0} max={1} step={0.1} value={s.min_score}
          onChange={(e) => set('min_score', Number(e.target.value))}
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          v3 answers with a score from 0.0 (almost certainly a bot) to 1.0, not a
          pass or a fail. 0.5 is Google&apos;s suggested starting point. At
          instance level a low score only MARKS a form submission in its inbox —
          nothing is refused on the strength of a score a project did not choose.
        </p>
      </Field>

      <TestBanner result={testResult} />

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving || testing}>
          {saving ? 'Saving…' : 'Save reCAPTCHA settings'}
        </button>
        <button
          className="btn btn-secondary" onClick={test}
          disabled={saving || testing || !s.has_secret}
          title={s.has_secret ? undefined : 'Save a secret key first'}
        >
          {testing ? 'Checking…' : 'Test secret key'}
        </button>
        {s.configured && (
          <button className="btn btn-secondary" onClick={clear} disabled={saving || testing}>
            Turn off
          </button>
        )}
      </div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: 0 }}>
        The test asks Google about the stored secret with a deliberately invalid
        token: a rejected SECRET and a rejected token are different answers, so a
        wrong key is caught here rather than by the first visitor whose enquiry
        it silently flags.
      </p>
    </div>
  )
}

/* ── Payment gateway ──────────────────────────────────────────────── */

/* ── Payments ─────────────────────────────────────────────────────────
 * Gateway credentials moved into Billing → Provider, so everything about
 * money — the plan catalog, prices, credits, coupons, tax, the gateway and
 * the webhook secret — is configured in one tab instead of two.
 * See pages/admin/billing/PaymentProviderPanel.tsx.
 */

/* ── Agent security ───────────────────────────────────────────────── */

/**
 * The token-authenticated agent surfaces (the SSH console and the laptop
 * agent) plus the auth-cookie Secure policy.
 *
 * Every field here replaces a value that used to be hardcoded, and every
 * "unset" state reproduces that old value exactly — so this panel is safe to
 * open, look at, and close. See docs/agent_surface_hardening_plan.md.
 */
interface AgentSecurityForm {
  maxTtl: string
  defaultTtl: string
  sessionTtl: string
  surfaces: string
  delegated: boolean
  cookie: string
}

/** Blank inputs mean UNSET, which is not the same as the resolved value — a
 *  session TTL showing 1800 because nobody chose it must not be saved back as
 *  an explicit 1800. `explicit` from the server is what tells them apart. */
function toAgentForm(p: AgentSecurityPolicy): AgentSecurityForm {
  const shown = (set: boolean | undefined, value: number | null) =>
    set && value != null ? String(value) : ''
  return {
    maxTtl: shown(p.explicit?.agent_token_max_ttl_days, p.agent_token_max_ttl_days),
    defaultTtl: shown(p.explicit?.agent_token_default_ttl_days, p.agent_token_default_ttl_days),
    sessionTtl: shown(p.explicit?.agent_session_ttl_seconds, p.agent_session_ttl_seconds),
    surfaces: p.agent_surfaces_enabled ?? '',
    delegated: !!p.agent_require_delegated_db,
    cookie: p.cookie_secure || 'auto',
  }
}

function AgentSecurityTab() {
  const [s, setS] = useState<AgentSecurityPolicy | null>(null)
  const [f, setF] = useState<AgentSecurityForm | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [expiring, setExpiring] = useState(false)

  useEffect(() => {
    getAgentSecurity()
      .then((p) => { setS(p); setF(toAgentForm(p)) })
      .catch(() => toast.error('Failed to load the agent security policy'))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s || !f) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof AgentSecurityForm>(k: K, v: AgentSecurityForm[K]) =>
    setF({ ...f, [k]: v })

  const surfaces = f.surfaces.split(',').filter(Boolean)
  const toggleSurface = (name: string, on: boolean) => {
    const next = on ? [...new Set([...surfaces, name])] : surfaces.filter((x) => x !== name)
    // Keep the canonical order so the value round-trips unchanged.
    set('surfaces', (s.surfaces || []).filter((x) => next.includes(x)).join(','))
  }

  const save = async () => {
    setSaving(true)
    try {
      // 0 / "" CLEAR a field back to its default — which is exactly what an
      // emptied input means here, so send it rather than omitting it.
      const saved = await updateAgentSecurity({
        agent_token_max_ttl_days: parseInt(f.maxTtl, 10) || 0,
        agent_token_default_ttl_days: parseInt(f.defaultTtl, 10) || 0,
        agent_session_ttl_seconds: parseInt(f.sessionTtl, 10) || 0,
        agent_surfaces_enabled: f.surfaces,
        agent_require_delegated_db: f.delegated,
        cookie_secure: f.cookie,
      })
      setS(saved)
      setF(toAgentForm(saved))
      toast.success('Agent security policy saved')
    } catch (e: unknown) {
      toast.error(errText(e, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  const expireTokens = async () => {
    if (!window.confirm(
      'Give every non-expiring agent token an expiry of NOW?\n\n'
      + 'Anyone holding one will have to mint a new token before their next '
      + 'session. Running agent sessions keep working until they end.',
    )) return
    setExpiring(true)
    try {
      const { expired } = await expireNonExpiringAgentTokens()
      toast.success(expired ? `${expired} token(s) expired` : 'No non-expiring tokens to expire')
    } catch (e: unknown) {
      toast.error(errText(e, 'Failed to expire the tokens'))
    } finally {
      setExpiring(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Agent access tokens</h3>
        <ApplyBadge apply="live" />
      </div>
      <InfoBox>
        The credentials the <strong>SSH console</strong> and the <strong>laptop agent</strong>{' '}
        (<code>twasta run</code>) authenticate with. Leave a field blank to keep this
        instance&rsquo;s existing behaviour.
      </InfoBox>

      <Field label="Maximum token lifetime (days)">
        <input
          type="number"
          min={1}
          value={f.maxTtl}
          onChange={(e) => set('maxTtl', e.target.value)}
          placeholder="no limit — non-expiring tokens permitted"
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          Rejects any mint asking for longer, and any request for a non-expiring token.
          Existing tokens are <strong>not</strong> expired retroactively — that would cut live
          sessions the moment you saved; use the button below to do it deliberately.
        </p>
      </Field>

      <Field label="Default token lifetime (days)">
        <input
          type="number"
          min={1}
          value={f.defaultTtl}
          onChange={(e) => set('defaultTtl', e.target.value)}
          placeholder="90 (the built-in default)"
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          Pre-filled when a user mints a token or approves a device login.
        </p>
      </Field>

      <div>
        <button className="btn btn-secondary" onClick={expireTokens} disabled={expiring}>
          {expiring ? 'Expiring…' : 'Expire all non-expiring tokens'}
        </button>
      </div>

      <h3 style={{ fontSize: '1.0625rem', margin: '0.75rem 0 0' }}>Sessions &amp; surfaces</h3>

      <Field label="Session idle timeout (seconds)">
        <input
          type="number"
          min={60}
          value={f.sessionTtl}
          onChange={(e) => set('sessionTtl', e.target.value)}
          placeholder={`${s.defaults?.agent_session_ttl_seconds ?? 1800}`}
          style={{ width: '100%' }}
        />
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
          A session with an agent run in flight never times out, however long the run takes.
        </p>
      </Field>

      <Field label="Enabled agent surfaces">
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {(s.surfaces || []).map((name) => (
            <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={surfaces.includes(name)}
                onChange={(e) => toggleSurface(name, e.target.checked)}
              />
              {name === 'ssh' ? 'SSH console' : name === 'askai' ? 'Ask-AI runs (remote callers)' : 'Laptop agent (remote)'}
            </label>
          ))}
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          A kill switch that needs no revocations: a disabled surface refuses every token at
          exchange, and re-enabling it brings the same tokens back. The in-app Ask-AI dock is
          not affected — it is authenticated by cookie, not by a token.
        </p>
      </Field>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
        <input
          type="checkbox"
          checked={f.delegated}
          onChange={(e) => set('delegated', e.target.checked)}
        />
        Require delegated database access on every surface
      </label>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '-0.5rem 0 0', lineHeight: 1.5 }}>
        The laptop surface always reaches the database through this server. Turn this on to do
        the same everywhere, so no agent subprocess anywhere holds a database credential. Costs
        one HTTP round trip per database call on the surfaces that did not need it.
      </p>

      <h3 style={{ fontSize: '1.0625rem', margin: '0.75rem 0 0' }}>Auth cookies</h3>

      <Field label="Cookie security">
        <select
          value={f.cookie}
          onChange={(e) => set('cookie', e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="auto">Auto — Secure when the request arrived over HTTPS</option>
          <option value="always">Always — force Secure</option>
          <option value="never">Never — plain HTTP deployments</option>
        </select>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          <strong>Auto</strong> reads <code>X-Forwarded-Proto</code> only from a proxy named in{' '}
          <code>TRUSTED_PROXY_IPS</code>, so nobody can set the flag from outside.
        </p>
      </Field>

      {f.cookie === 'always' && (
        <InfoBox>
          <strong>Always</strong> on an instance that is not actually served over HTTPS locks
          everyone out: the browser silently drops the cookie and every login appears to
          succeed and then fail. Only choose it when TLS terminates somewhere this app cannot
          observe.
        </InfoBox>
      )}

      <div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save agent security policy'}
        </button>
      </div>
    </div>
  )
}

/* ── Analytics ────────────────────────────────────────────────────── */

function AnalyticsTab() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<InstanceAnalytics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    getInstanceAnalytics(days)
      .then(setData)
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false))
  }, [days])

  if (loading || !data) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const { engagement, revenue, tokens } = data

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <Section title="User engagement">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <Stat label="Total users" value={engagement.total_users.toLocaleString()} />
          <Stat label="Active users" value={engagement.active_users.toLocaleString()} />
          <Stat label="New signups" value={engagement.new_signups.toLocaleString()} />
        </div>
        {engagement.plan_distribution.length > 0 && (
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {engagement.plan_distribution.map((p) => (
              <span key={p.plan} style={chipStyle}>{p.label}: <strong>{p.count}</strong></span>
            ))}
          </div>
        )}
        {engagement.signups_daily.length > 0 && (
          <Bars data={engagement.signups_daily.map((d) => ({ date: d.date, value: d.count, label: `${d.date}: ${d.count} signups` }))} />
        )}
      </Section>

      <Section title="Revenue (recorded payments)">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <Stat label="Total revenue" value={`₹${revenue.total.toLocaleString()}`} />
          <Stat label="Paying customers" value={revenue.paying_customers.toLocaleString()} />
        </div>
        {revenue.daily.length > 0 && (
          <Bars data={revenue.daily.map((d) => ({ date: d.date, value: d.amount, label: `${d.date}: ₹${d.amount}` }))} accent="#009974" />
        )}
      </Section>

      <Section title="Token consumption & cost">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <Stat label="Total tokens" value={tokens.total_tokens.toLocaleString()} />
          <Stat label="Platform-key tokens" value={tokens.platform_tokens.toLocaleString()} />
          <Stat label="Own-key tokens" value={tokens.own_key_tokens.toLocaleString()} />
          <Stat label="Total cost" value={`$${tokens.total_cost_usd.toFixed(2)}`} />
          <Stat label="Platform cost" value={`$${tokens.platform_cost_usd.toFixed(2)}`} />
          <Stat label="Calls" value={tokens.calls.toLocaleString()} />
        </div>
        {tokens.daily.length > 0 && (
          <Bars data={tokens.daily.map((d) => ({ date: d.date, value: d.cost_usd, label: `${d.date}: $${d.cost_usd.toFixed(4)} / ${d.tokens.toLocaleString()} tok` }))} accent="#8b5cf6" />
        )}
        {tokens.by_model.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr><Th>Model</Th><Th>Provider</Th><Th right>Tokens</Th><Th right>Cost</Th><Th right>Calls</Th></tr>
            </thead>
            <tbody>
              {tokens.by_model.map((m) => (
                <tr key={`${m.provider}/${m.model}`}>
                  <Td>{m.model}</Td><Td>{m.provider}</Td>
                  <Td right>{m.tokens.toLocaleString()}</Td>
                  <Td right>${m.cost_usd.toFixed(4)}</Td>
                  <Td right>{m.calls}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  )
}

/* ── IOFlow integration health ────────────────────────────────────── */

function IOFlowHealthTab() {
  const [days, setDays] = useState(7)
  const [health, setHealth] = useState<IOFlowHealth | null>(null)
  const [failures, setFailures] = useState<IOFlowFailure[]>([])
  const [loading, setLoading] = useState(true)
  const [retrying, setRetrying] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    Promise.all([getIOFlowHealth(days), getIOFlowFailures({ limit: 50 })])
      .then(([h, f]) => {
        setHealth(h)
        setFailures(f.items)
      })
      .catch(() => toast.error('Failed to load IOFlow health'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [days]) // eslint-disable-line react-hooks/exhaustive-deps

  const retry = async (owner: string, project: string) => {
    const key = `${owner}|${project}`
    setRetrying(key)
    try {
      const { queued } = await retryIOFlowDelivery(owner, project)
      toast[queued ? 'success' : 'error'](
        queued ? `Queued delivery for ${project}` : `Nothing to deliver for ${project}`,
      )
    } catch {
      toast.error('Retry failed')
    } finally {
      setRetrying(null)
    }
  }

  if (loading || !health) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const { pool, totals, projects } = health

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{
          fontSize: '0.875rem',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          color: pool.running ? '#009974' : 'var(--color-text-secondary)',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: pool.running ? '#009974' : 'var(--color-text-secondary)',
            display: 'inline-block',
          }} />
          Delivery pool {pool.running ? 'running' : 'idle'}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn" onClick={load} title="Refresh" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </div>

      <Section title="Delivery pool">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
          <Stat label="Workers" value={String(pool.pool_size ?? '—')} />
          <Stat label="Active projects" value={String(pool.active_projects ?? 0)} />
          <Stat label="Queued now" value={String(pool.ready_depth ?? 0)} />
          <Stat label="Draining" value={String(pool.inflight ?? 0)} />
          <Stat label="Awaiting retry" value={String(pool.retry_queued ?? 0)} />
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
          Delivery is wake-driven: a project's database is read only when a save signals pending events or a backoff
          retry comes due — never on a fixed timer.
        </p>
      </Section>

      <Section title={`Delivery failures (last ${health.days} day${health.days === 1 ? '' : 's'})`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <Stat label="Incidents" value={totals.incidents.toLocaleString()} />
          <Stat label="Failed events" value={totals.failed.toLocaleString()} />
          <Stat label="Permanently parked" value={totals.terminal.toLocaleString()} />
          <Stat label="Projects affected" value={totals.projects_affected.toLocaleString()} />
        </div>

        {projects.length === 0 ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.875rem' }}>
            No delivery failures in this window. ✅
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Project</Th><Th>Owner</Th><Th right>Incidents</Th>
                <Th right>Failed</Th><Th right>Parked</Th><Th>Last failure</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const key = `${p.owner_user_id}|${p.project_id}`
                return (
                  <tr key={key}>
                    <Td>{p.project_id}</Td>
                    <Td><span title={p.owner_user_id}>{p.owner_user_id.slice(0, 8)}…</span></Td>
                    <Td right>{p.incidents}</Td>
                    <Td right>{p.failed}</Td>
                    <Td right style={p.terminal > 0 ? { color: '#dc2626', fontWeight: 600 } : undefined}>{p.terminal}</Td>
                    <Td>{p.last_at ? new Date(p.last_at).toLocaleString() : '—'}</Td>
                    <Td>
                      <button
                        className="btn"
                        disabled={retrying === key}
                        onClick={() => retry(p.owner_user_id, p.project_id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8125rem' }}
                      >
                        <RefreshCw size={12} /> {retrying === key ? 'Queuing…' : 'Retry'}
                      </button>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Section>

      {failures.length > 0 && (
        <Section title="Recent failure detail">
          <table style={tableStyle}>
            <thead>
              <tr><Th>When</Th><Th>Project</Th><Th>Event</Th><Th right>Attempts</Th><Th>Error</Th></tr>
            </thead>
            <tbody>
              {failures.map((f) => (
                <tr key={f.id}>
                  <Td>{f.created_at ? new Date(f.created_at).toLocaleString() : '—'}</Td>
                  <Td>{f.project_id}</Td>
                  <Td>{f.sample_event || '—'}</Td>
                  <Td right>{f.attempts_max}</Td>
                  <Td><span style={{ color: 'var(--color-text-secondary)' }} title={f.error || ''}>{(f.error || '').slice(0, 80) || '—'}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  )
}

/* ── Access & Waitlist ────────────────────────────────────────────── */

function AccessTab() {
  const [stage, setStage] = useState<ReleaseStageInfo | null>(null)
  const [savingStage, setSavingStage] = useState(false)
  const [items, setItems] = useState<WaitlistEntry[]>([])
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'GRANTED' | ''>('PENDING')
  const [accountFilter, setAccountFilter] = useState<WaitlistAccountState | ''>('')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [granting, setGranting] = useState<number | null>(null)

  const loadStage = () => {
    getReleaseStage()
      .then(setStage)
      .catch(() => toast.error('Failed to load the release stage'))
  }

  const loadWaitlist = () => {
    setLoading(true)
    getWaitlist({
      status: statusFilter || undefined,
      account: accountFilter || undefined,
      q: q.trim() || undefined,
    })
      .then((res) => {
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((e: unknown) => toast.error(errText(e, 'Failed to load the waitlist')))
      .finally(() => setLoading(false))
  }

  useEffect(loadStage, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadWaitlist, [statusFilter, accountFilter])

  const changeStage = async (next: string) => {
    if (next === stage?.release_stage) return
    const openingUp = next === 'production'
    const ok = window.confirm(
      openingUp
        ? 'Switch to PRODUCTION? Anyone will be able to create their own account from the login page.'
        : 'Switch back to BETA? Self-service sign-up will be closed — new users can only join the waitlist.'
    )
    if (!ok) return
    setSavingStage(true)
    try {
      const updated = await updateReleaseStage(next)
      setStage({ ...updated, pending_waitlist: stage?.pending_waitlist })
      clearAppConfigCache()  // login/register pages pick up the new stage
      toast.success(`Release stage set to ${updated.release_stage}`)
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to update the release stage'
      toast.error(msg)
    } finally {
      setSavingStage(false)
    }
  }

  const grant = async (entry: WaitlistEntry) => {
    const ok = window.confirm(
      `Grant access to ${entry.email}?\n\nThis creates their account (admin of their own workspace) and emails an activation link.`
    )
    if (!ok) return
    setGranting(entry.id)
    try {
      const res = await grantWaitlistAccess(entry.id)
      if (res.email_sent) toast.success(res.message)
      else toast.error(res.message)
      loadWaitlist()
      loadStage()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to grant access'
      toast.error(msg)
    } finally {
      setGranting(null)
    }
  }

  const isBeta = stage?.release_stage !== 'production'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <Section title="Release stage">
        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: '1rem',
          maxWidth: 640,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Rocket size={16} style={{ color: 'var(--color-accent)' }} />
            <select
              value={stage?.release_stage || 'beta'}
              disabled={!stage || savingStage}
              onChange={(e) => changeStage(e.target.value)}
              style={{ minWidth: 180 }}
            >
              <option value="beta">Beta — waitlist only</option>
              <option value="production">Production — open sign-up</option>
            </select>
            <span style={chipStyle}>
              {isBeta ? 'Sign-up closed' : 'Sign-up open'}
            </span>
            {savingStage && <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
          </div>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.625rem 0 0' }}>
            In <strong>beta</strong> the login page offers only “Join the waitlist” and account
            creation is rejected — grant access from the waitlist below. In{' '}
            <strong>production</strong> anyone can create their own account.
          </p>
        </div>
      </Section>

      <Section title={`Waitlist${total ? ` (${total})` : ''}`}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'PENDING' | 'GRANTED' | '')}
            title="The grant — what the admin did"
          >
            <option value="PENDING">Not granted</option>
            <option value="GRANTED">Granted</option>
            <option value="">Any grant</option>
          </select>
          {/* Orthogonal to the grant: what the invited person did with it. */}
          <select
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value as WaitlistAccountState | '')}
            title="The account — what the invited person did"
          >
            <option value="">Any account state</option>
            <option value="none">No account yet</option>
            <option value="pending">Not activated</option>
            <option value="active">Activated</option>
            <option value="deactivated">Deactivated</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Search size={14} style={{ color: 'var(--color-text-secondary)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadWaitlist() }}
              placeholder="Search email / name / organization"
              style={{ minWidth: 260 }}
            />
          </div>
          <button className="btn btn-secondary" onClick={loadWaitlist} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.875rem' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.875rem' }}>
            No waitlist entries {statusFilter ? `with status ${statusFilter.toLowerCase()}` : ''}.
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Organization</Th>
                <Th>Country</Th>
                <Th>What they want to build</Th>
                <Th>Joined</Th>
                <Th>Access</Th>
                <Th>Account</Th>
                <Th right />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <Td>{e.name}</Td>
                  <Td>{e.email}</Td>
                  <Td>
                    <span title={e.country
                      ? `Bills in ${e.country === 'IN' ? 'INR' : 'USD'}`
                      : 'No country on file — will bill in USD'}>
                      {e.country ? countryName(e.country) : '—'}
                    </span>
                  </Td>
                  <Td style={{ maxWidth: 260 }}>
                    <span title={e.application_idea} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.application_idea}
                    </span>
                  </Td>
                  <Td>{e.created_at ? e.created_at.slice(0, 10) : '—'}</Td>
                  <Td>
                    <span
                      style={{
                        ...chipStyle,
                        color: e.status === 'GRANTED' ? 'var(--color-success, #009974)' : 'var(--color-text-secondary)',
                      }}
                      title={e.granted_at ? `Granted on ${e.granted_at.slice(0, 10)}` : 'Not granted yet'}
                    >
                      {e.status === 'GRANTED' ? 'Granted' : 'Pending'}
                    </span>
                  </Td>
                  <Td><AccountStateChip entry={e} /></Td>
                  <Td right>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => grant(e)}
                      disabled={granting === e.id || e.account_state === 'active'}
                      title={
                        e.account_state === 'active'
                          ? 'Already activated — there is nothing left to send'
                          : e.account_state === 'deactivated'
                            ? 'This account is deactivated — re-enable it in the Users tab'
                            : e.status === 'GRANTED'
                              ? 'Re-send the activation link with a fresh code'
                              : 'Create the account and email an activation link'
                      }
                    >
                      {granting === e.id
                        ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                        : <UserPlus size={13} />}
                      {e.status === 'GRANTED' ? 'Resend' : 'Grant access'}
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  )
}

/**
 * Did the invited person accept? The grant chip next to this one says what the
 * ADMIN did; this one says what the PERSON did with it — an entry can sit at
 * "Granted / Not activated" for weeks, which is exactly the case an admin needs
 * to spot before chasing or re-sending.
 */
function AccountStateChip({ entry }: { entry: WaitlistEntry }) {
  const seen = entry.last_login_at ? ` · last sign-in ${entry.last_login_at.slice(0, 10)}` : ''
  const [label, color, title]: [string, string, string] =
    entry.account_state === 'active'
      ? ['Activated', 'var(--color-success, #009974)',
         `Accepted the invitation and activated their account${seen}`]
      : entry.account_state === 'pending'
        ? ['Not activated', 'var(--color-warning, #b7791f)',
           entry.status === 'GRANTED'
             ? 'Account created and the link sent — not used yet'
             : 'Registered but never activated']
        : entry.account_state === 'deactivated'
          ? ['Deactivated', 'var(--color-error, #d64545)',
             `Activated, then switched off in the Users tab${seen}`]
          : ['No account', 'var(--color-text-secondary)',
             'No account exists for this address yet']
  return (
    <span style={{ ...chipStyle, color, whiteSpace: 'nowrap' }} title={title}>
      {label}
    </span>
  )
}

/**
 * Platform-wide user directory. An instance admin can deactivate ANY account
 * on the instance, in any tenant — a deactivated user is refused at every
 * credential path (sign-in, session takeover, activation, token refresh,
 * password reset) and any live session is dropped immediately.
 *
 * Tenant (enterprise) admins have the same control scoped to their OWN tenant
 * only, at Team Management (/enterprise/users).
 */
const USERS_PAGE_SIZE = 50

function UsersTab() {
  const [items, setItems] = useState<InstanceUser[]>([])
  const [tenants, setTenants] = useState<{ tenant_id: string; tenant_name: string }[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [q, setQ] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  const load = (nextOffset = offset, search = q) => {
    setLoading(true)
    getInstanceUsers({
      q: search.trim() || undefined,
      tenant_id: tenantId || undefined,
      status: statusFilter || undefined,
      limit: USERS_PAGE_SIZE,
      offset: nextOffset,
    })
      .then((res) => {
        setItems(res.items)
        setTotal(res.total)
        setTenants(res.tenants)
        setOffset(res.offset)
      })
      .catch((e: unknown) => {
        const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        toast.error(msg || 'Failed to load users')
      })
      .finally(() => setLoading(false))
  }

  // Filter changes always restart at page 1; the search box is applied on
  // Enter / Search so we aren't firing a request per keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(0) }, [tenantId, statusFilter])

  const toggle = async (u: InstanceUser) => {
    const deactivating = u.is_active
    const ok = window.confirm(
      deactivating
        ? `Deactivate ${u.user_name} (${u.email})?\n\nThey will be signed out immediately and cannot log in to twasta.ai until reactivated.`
        : `Reactivate ${u.user_name} (${u.email})?\n\nThey will be able to log in again.`
    )
    if (!ok) return
    setBusy(u.user_id)
    try {
      const res = await setInstanceUserStatus(u.user_id, !u.is_active)
      toast.success(
        deactivating
          ? `${u.user_name} deactivated${res.sessions_ended ? ' and signed out' : ''}`
          : `${u.user_name} reactivated`
      )
      load()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Failed to update the user')
    } finally {
      setBusy(null)
    }
  }

  const unlock = async (u: InstanceUser) => {
    setBusy(u.user_id)
    try {
      await unlockInstanceUser(u.user_id)
      toast.success(`${u.user_name} can sign in again`)
      load()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Failed to unlock the account')
    } finally {
      setBusy(null)
    }
  }

  const resetMfa = async (u: InstanceUser) => {
    if (!window.confirm(`Reset two-factor sign-in for ${u.user_name} (${u.email})?\n\nThey are signed out and set up a new authenticator at their next sign-in.`)) return
    setBusy(u.user_id)
    try {
      await resetInstanceUserMfa(u.user_id)
      toast.success(`Two-factor sign-in reset for ${u.user_name}`)
      load()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Failed to reset two-factor sign-in')
    } finally {
      setBusy(null)
    }
  }

  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + items.length, total)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <Section title={`Users${total ? ` (${total})` : ''}`}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 0.875rem' }}>
          Deactivating an account blocks sign-in across the whole instance and ends any
          live session at once. Tenant admins can do the same for their own tenant from
          Team Management.
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">All tenants</option>
            {tenants.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | '')}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Deactivated</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Search size={14} style={{ color: 'var(--color-text-secondary)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(0) }}
              placeholder="Search name / email"
              style={{ minWidth: 240 }}
            />
          </div>
          <button className="btn btn-secondary" onClick={() => load(0)} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.875rem' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.875rem' }}>
            No users match these filters.
          </p>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Tenant</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Last login</Th>
                  <Th right />
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  // Self and fellow platform operators are protected server-side
                  // too — the toggle is disabled so it never round-trips a 400.
                  const locked = u.is_current_user || (u.is_instance_admin && u.is_active)
                  return (
                    <tr key={u.user_id}>
                      <Td>
                        {u.user_name}
                        {u.is_current_user && <span style={{ ...chipStyle, marginLeft: '0.4rem', fontSize: '0.8125rem' }}>You</span>}
                        {u.is_instance_admin && !u.is_current_user && (
                          <span style={{ ...chipStyle, marginLeft: '0.4rem', fontSize: '0.8125rem' }}>Platform admin</span>
                        )}
                      </Td>
                      <Td>{u.email}</Td>
                      <Td>{u.tenant_name || '—'}</Td>
                      <Td>{u.role_display}</Td>
                      <Td>
                        <span
                          style={{
                            ...chipStyle,
                            color: u.is_active ? 'var(--color-success, #009974)' : 'var(--color-error, #d64545)',
                          }}
                          title={
                            !u.is_active && u.deactivated_at
                              ? `Deactivated ${u.deactivated_at.slice(0, 10)}${u.deactivated_by_name ? ` by ${u.deactivated_by_name}` : ''}`
                              : u.status === 'N' ? 'Registered but email not yet verified' : undefined
                          }
                        >
                          {u.is_active ? (u.status === 'N' ? 'Unverified' : 'Active') : 'Deactivated'}
                        </span>
                        {u.mfa_enabled && (
                          <span style={{ ...chipStyle, marginLeft: '0.4rem' }} title="Two-factor sign-in is on">
                            2FA
                          </span>
                        )}
                        {u.locked && (
                          <span
                            style={{ ...chipStyle, marginLeft: '0.4rem', color: 'var(--color-error, #d64545)' }}
                            title="Locked after too many failed sign-ins"
                          >
                            Locked
                          </span>
                        )}
                      </Td>
                      <Td>{u.last_login_at ? u.last_login_at.slice(0, 10) : 'Never'}</Td>
                      <Td right>
                        {u.mfa_enabled && !u.is_current_user && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => resetMfa(u)}
                            disabled={busy === u.user_id}
                            title="Lost phone? Remove their authenticator"
                          >
                            Reset 2FA
                          </button>
                        )}
                        {u.locked && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => unlock(u)}
                            disabled={busy === u.user_id}
                            title="Clear the sign-in lock"
                          >
                            Unlock
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => toggle(u)}
                          disabled={busy === u.user_id || locked}
                          title={
                            u.is_current_user ? 'You cannot deactivate your own account'
                              : u.is_instance_admin && u.is_active
                                ? 'Platform administrators must be removed from INSTANCE_ADMIN_IDS first'
                                : u.is_active ? 'Deactivate this user' : 'Reactivate this user'
                          }
                        >
                          {busy === u.user_id
                            ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                            : u.is_active
                              ? <ToggleRight size={16} style={{ color: 'var(--color-success, #009974)' }} />
                              : <ToggleLeft size={16} style={{ color: 'var(--color-text-secondary)' }} />}
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                {from}–{to} of {total}
              </span>
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => load(Math.max(0, offset - USERS_PAGE_SIZE))}
                disabled={offset === 0 || loading}
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => load(offset + USERS_PAGE_SIZE)}
                disabled={to >= total || loading}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </>
        )}
      </Section>
    </div>
  )
}

/* ── Organizations (tenants) ─────────────────────────────────────────────
 * Every tenant on the instance, editable by a platform operator. The editor
 * itself is the SAME modal a tenant's own enterprise admin gets from Team
 * Management, hitting the same `PUT /api/enterprise/tenant` — this tab only
 * adds the cross-tenant directory and passes the tenant id.
 */
function TenantsTab() {
  const [items, setItems] = useState<InstanceTenant[]>([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)

  const load = async (search = q) => {
    setLoading(true)
    try {
      const res = await getInstanceTenants(search || undefined)
      setItems(res.items)
    } catch {
      toast.error('Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load('') }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <Section title={`Organizations${items.length ? ` (${items.length})` : ''}`}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 0.875rem' }}>
          Name, email domain, website, tax registration and the ibase SSO
          <strong> enterprise </strong> each organization is mapped to. An SSO user
          arriving with an enterprise value is placed in whichever organization
          claims it, so a value can be mapped only once. An organization&rsquo;s own
          enterprise admin can edit the same details from Team Management.
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Search size={14} style={{ color: 'var(--color-text-secondary)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load() }}
              placeholder="Search name / domain / enterprise"
              style={{ minWidth: 260 }}
            />
          </div>
          <button className="btn btn-secondary" onClick={() => load()} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.875rem' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.875rem' }}>
            No organizations match this search.
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Organization</Th>
                <Th>Email domain</Th>
                <Th>Enterprise (SSO)</Th>
                <Th>Owner</Th>
                <Th>Users</Th>
                <Th right />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.tenant_id}>
                  <Td>
                    {t.tenant_name || '—'}
                    {t.web_site && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {t.web_site}
                      </div>
                    )}
                  </Td>
                  <Td>{t.email_domain || '—'}</Td>
                  <Td>
                    {t.enterprise
                      ? <span style={chipStyle}>{t.enterprise}</span>
                      : <span style={{ color: 'var(--color-text-secondary)' }}>Unmapped</span>}
                  </Td>
                  <Td>
                    {t.owner_name || '—'}
                    {t.owner_email && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {t.owner_email}
                      </div>
                    )}
                  </Td>
                  <Td>{t.user_count}</Td>
                  <Td right>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setEditing(t.tenant_id)}
                      title="Edit this organization's details"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <TenantDetailsModal
          open
          tenantId={editing}
          onClose={() => setEditing(null)}
          // Patch the row in place rather than refetching the whole directory.
          onSaved={(saved) => setItems((rows) => rows.map((r) => (
            r.tenant_id === saved.tenant_id ? { ...r, ...saved } : r
          )))}
        />
      )}
    </div>
  )
}


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 style={{ fontSize: '1.0625rem', marginBottom: '0.75rem' }}>{title}</h3>
      {children}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
      <div style={{ fontSize: '1.3125rem', fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{label}</div>
    </div>
  )
}

const chipStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  padding: '0.25rem 0.6rem',
  borderRadius: '999px',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '0.875rem',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
}

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <th style={{ textAlign: right ? 'right' : 'left', padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>{children}</th>
}
function Td({ children, right, style }: { children: React.ReactNode; right?: boolean; style?: React.CSSProperties }) {
  return <td style={{ textAlign: right ? 'right' : 'left', padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--color-border)', ...style }}>{children}</td>
}

function Bars({ data, accent = 'var(--color-accent)' }: { data: Array<{ date: string; value: number; label: string }>; accent?: string }) {
  const max = Math.max(...data.map((d) => d.value), 0.0001)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, marginTop: '0.875rem' }}>
      {data.map((d) => (
        <div key={d.date} title={d.label} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ width: '100%', maxWidth: 24, height: `${Math.max((d.value / max) * 100, 2)}%`, background: accent, borderRadius: '2px 2px 0 0', opacity: 0.8, transition: 'height 300ms ease' }} />
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', transform: 'rotate(-45deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// IOFlow connector publish approvals
//
// A tenant-scoped connector belongs to the tenant that made it and needs no
// approval. A GLOBAL one is shared with every tenant on the IOFlow instance,
// so IOFlow gates it on INSTANCE_ADMIN. When an Ask-AI agent needs one and the
// user is not an instance admin, its work pauses and the request lands here.
//
// Approving publishes with YOUR IOFlow credentials — being a Twasta instance
// admin is not on its own enough, IOFlow checks its own role — and then resumes
// the requester's paused session automatically.
// ---------------------------------------------------------------------------

function IOFlowApprovalsTab() {
  const [rows, setRows] = useState<IOFlowPublishRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState('')
  const [expanded, setExpanded] = useState('')
  const [showAll, setShowAll] = useState(false)

  const load = () => {
    setLoading(true)
    getIOFlowPublishRequests(showAll ? '' : 'pending')
      .then((r) => setRows(r.requests || []))
      .catch((e) => toast.error(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false))
  }

  useEffect(load, [showAll])

  const approve = async (row: IOFlowPublishRequest) => {
    setBusyId(row.id)
    try {
      const res = await approveIOFlowPublish(row.id)
      if (res.ok) {
        toast.success(
          `Published "${row.app_key}"` +
            (row.resumable ? ' — the requester’s agent is continuing.' : '.'),
        )
      } else {
        // Deliberately not a silent failure: the decision stands, the install
        // did not, and the request stays pending so it can be retried.
        toast.error(res.message || res.error || 'The publish failed.')
      }
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || String(e))
    } finally {
      setBusyId('')
    }
  }

  const deny = async (row: IOFlowPublishRequest) => {
    const reason = window.prompt(
      `Why is "${row.app_key}" being refused? The requester’s agent is told, so ` +
        'it can fall back to a tenant-scoped connector instead of waiting.',
      '',
    )
    if (reason === null) return
    setBusyId(row.id)
    try {
      await denyIOFlowPublish(row.id, reason)
      toast.success('Request denied.')
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || String(e))
    } finally {
      setBusyId('')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.0625rem' }}>Connector publish requests</h3>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.9375rem' }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Include decided
        </label>
      </div>

      <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginTop: 0 }}>
        A <strong>global</strong> connector is shared with every tenant on the
        IOFlow instance. Approving publishes it with <em>your</em> IOFlow
        credentials and resumes the requester’s paused agent session.
      </p>

      {rows.length === 0 && !loading && (
        <p style={{ fontSize: '0.9375rem' }}>Nothing waiting.</p>
      )}

      {rows.map((r) => (
        <div
          key={r.id}
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem',
            marginBottom: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong>{r.app_name || r.app_key}</strong>
            <code style={{ fontSize: '0.875rem' }}>{r.app_key}</code>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              · {r.project} · {r.status}
              {r.expires_at ? ` · expires ${new Date(r.expires_at).toLocaleString()}` : ''}
            </span>
          </div>

          {r.justification && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.9375rem' }}>{r.justification}</p>
          )}

          {!r.resumable && r.status === 'pending' && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              No paused session was recorded for this request, so approving will
              publish the connector but nothing will resume automatically.
            </p>
          )}
          {r.install_error && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.875rem', color: 'var(--color-error, #f44336)' }}>
              Last publish attempt failed: {r.install_error}
            </p>
          )}
          {r.resume_error && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.875rem', color: 'var(--color-error, #f44336)' }}>
              Could not resume the requester’s session: {r.resume_error}
            </p>
          )}
          {r.denial_reason && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.875rem' }}>
              Denied: {r.denial_reason}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setExpanded(expanded === r.id ? '' : r.id)}
            >
              {expanded === r.id ? 'Hide' : 'Review'} definition
            </button>
            {r.status === 'pending' && (
              <>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={busyId === r.id}
                  onClick={() => approve(r)}
                >
                  <ShieldCheck size={12} /> Approve &amp; publish
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={busyId === r.id}
                  onClick={() => deny(r)}
                >
                  Deny
                </button>
              </>
            )}
          </div>

          {expanded === r.id && (
            <pre
              style={{
                marginTop: '0.5rem',
                maxHeight: '20rem',
                overflow: 'auto',
                background: 'var(--color-bg-secondary)',
                padding: '0.5rem',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.8125rem',
              }}
            >
              {r.seed_json}
            </pre>
          )}
        </div>
      ))}
    </div>
  )
}
